import { UseQueryOptions } from '@tanstack/react-query';
import { PaginationState } from '@tanstack/react-table';
import { useQuery, useMutation } from 'data/utils/useQuery';
import { useDataConfig } from 'data/utils/hooks';
import { QueryKeys } from 'data/constants';
import {
  RemoteDomainDto,
  RemoteDomainControllerService,
  RemoteDomainStatusControllerService,
  DomainStatusDto,
  ScheduledPruneDto,
  RemoteDomainPruneControllerService,
  type RemoteDomainFilterResponseDto,
} from 'data/openapi';

export const useRemoteDomains = (
  options: UseQueryOptions<RemoteDomainFilterResponseDto, any, RemoteDomainFilterResponseDto, any>,
  pagination?: PaginationState,
  healthStatus?: boolean,
  status?: RemoteDomainDto.phase,
  name?: string,
) => {
  let from: number | undefined;
  let limit: number | undefined;
  if (pagination) {
    const { pageSize, pageIndex } = pagination as PaginationState;
    from = pageIndex;
    limit = pageSize;
  }
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.REMOTE_DOMAINS, from ?? null, limit ?? null, healthStatus, status, name],
    () =>
      RemoteDomainControllerService.listRemoteDomains(
        healthStatus,
        from && limit ? from * limit : undefined,
        status,
        limit,
        name,
      ),
    {
      select: ({ domains, totalCount }) => {
        const list = domains?.map((el) => {
          return {
            value: el.name,
            label: el.name,
            ...el,
          };
        });

        return { domains: list, totalCount };
      },
      refetchInterval: pollingInterval,
      keepPreviousData: true,
      ...options,
    },
  );
};

export const useRemoteDomain = (
  name: string,
  options?: UseQueryOptions<RemoteDomainDto, any, RemoteDomainDto, any>,
) => {
  const { pollingInterval } = useDataConfig();
  return useQuery([QueryKeys.REMOTE_DOMAINS, name], () => RemoteDomainControllerService.getRemoteDomain(name), {
    refetchInterval: pollingInterval,
    ...options,
  });
};

export const useRemoteDomainHealthStatus = (name: string, options?: UseQueryOptions<unknown, any, DomainStatusDto>) => {
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.REMOTE_DOMAINS, name, 'status'],
    () => RemoteDomainStatusControllerService.getRemoteDomainHealthStatus(name),
    {
      refetchInterval: pollingInterval,
      ...options,
    },
  );
};

export const useCreateRemoteDomain = () => {
  return useMutation(async (values: RemoteDomainDto) => {
    await RemoteDomainControllerService.createRemoteDomain(values);
  });
};

export const useDeleteRemoteDomain = () => {
  return useMutation(async (name: string) => {
    await RemoteDomainControllerService.deleteRemoteDomain(name);
  });
};

export const useUpdateRemoteDomain = () => {
  return useMutation(async (values: RemoteDomainDto) => {
    await RemoteDomainControllerService.updateRemoteDomain(values);
  });
};

export const useRemoteDomainPruningSchedules = (
  name: string,
  component: string,
  options?: UseQueryOptions<unknown, any, ScheduledPruneDto>,
) => {
  return useQuery(
    [QueryKeys.REMOTE_DOMAINS, name, QueryKeys.PRUNE, component],
    () => RemoteDomainPruneControllerService.getRemoteDomainPruningSchedule(name, component),
    options,
  );
};

export const useCreateRemoteDomainPruningSchedule = (name: string) => {
  return useMutation(async ({ component, values }: { component: string; values: ScheduledPruneDto }) => {
    await RemoteDomainPruneControllerService.createRemoteDomainPruningSchedule(name, component, values);
  });
};

export const useDeleteRemoteDomainPruningSchedule = () => {
  return useMutation(async ({ name, component }: { name: string; component: string }) => {
    await RemoteDomainPruneControllerService.deleteRemoteDomainPruningSchedule(name, component);
  });
};
